/**
 * sl-ufq6.3.2: Automation Budget Group Selector
 *
 * Dropdown for assigning an automation to a budget group.
 * Persists selection via REST API.
 */
import { notify } from '@affine/component';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';

interface GroupOption {
  id: string;
  name: string;
}

export const AutomationGroupSelector = ({
  workspaceId,
  automationBlockId,
}: {
  workspaceId: string;
  automationBlockId?: string;
}) => {
  const t = useI18n();
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const budgetBase = `/api/workspace/${workspaceId}/ai/budget-limits`;

  useEffect(() => {
    if (!workspaceId || !automationBlockId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const response = await workspaceApiFetch(
          budgetBase + '/automation-group/' + automationBlockId,
          { credentials: 'include' },
          backgroundPolicy(workspaceId, 'automation-group:' + automationBlockId)
        );
        if (!response.ok) throw new Error(await response.text());
        const data: { currentGroupId: string | null; options: GroupOption[] } =
          await response.json();
        setGroups(data.options);
        setSelectedGroupId(data.currentGroupId ?? '');
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [workspaceId, automationBlockId, budgetBase]);

  const handleGroupChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const groupId = e.target.value;
      if (!automationBlockId) return;

      const previousGroupId = selectedGroupId;
      setSelectedGroupId(groupId);

      const request = groupId
        ? workspaceApiFetch(
            `${budgetBase}/automation-group/${automationBlockId}`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ groupId }),
            },
            foregroundPolicy(
              workspaceId,
              `automation-group-update:${automationBlockId}`
            )
          )
        : workspaceApiFetch(
            `${budgetBase}/automation-group/${automationBlockId}`,
            {
              method: 'DELETE',
              credentials: 'include',
            },
            foregroundPolicy(
              workspaceId,
              `automation-group-delete:${automationBlockId}`
            )
          );

      void request
        .then(async response => {
          if (!response.ok) throw new Error(await response.text());
        })
        .catch(error => {
          setSelectedGroupId(previousGroupId);
          notify.error({
            title:
              t['com.streamlain.streamlain.automation-group.update-failed'](),
            message: error instanceof Error ? error.message : undefined,
          });
        });
    },
    [automationBlockId, budgetBase, selectedGroupId, t, workspaceId]
  );

  if (loading || groups.length === 0 || !automationBlockId) {
    return null;
  }

  return (
    <div
      style={{
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        color: 'var(--streamlain-v2-text-secondary)',
      }}
    >
      <span style={{ whiteSpace: 'nowrap' }}>
        {t['com.streamlain.streamlain.automation-group.label']()}
      </span>
      <select
        value={selectedGroupId}
        onChange={handleGroupChange}
        style={{
          flex: 1,
          padding: '4px 8px',
          borderRadius: 4,
          border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
          background: 'var(--streamlain-v2-layer-background-primary)',
          color: 'var(--streamlain-v2-text-primary)',
          fontSize: 13,
        }}
      >
        <option value="">
          {t['com.streamlain.streamlain.automation-group.default']()}
        </option>
        {groups.map(g => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
    </div>
  );
};

function backgroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'settings' as const,
    key,
    mode: 'background' as const,
    priority: 'background' as const,
  };
}

function foregroundPolicy(workspaceId: string, key: string) {
  return {
    workspaceId,
    category: 'foreground-action' as const,
    key,
    mode: 'foreground' as const,
    priority: 'foreground' as const,
  };
}
